import React, { useState } from 'react'
import { Search, CheckCircle, XCircle, Loader, Download, Copy } from 'lucide-react'
import { checkUID, validateUIDFormat } from '../services/api'

const UIDChecker = () => {
  const [uid, setUid] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [copied, setCopied] = useState({})

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setResult(null)

    // Format-Validierung
    if (!uid.trim()) {
      setError('Bitte geben Sie eine UID ein.')
      return
    }

    if (!validateUIDFormat(uid.trim())) {
      setError('Ungültiges UID-Format. Bitte verwenden Sie das Format: DE123456789.')
      return
    }

    setLoading(true)
    
    try {
      const response = await checkUID(uid.trim())
      setResult(response)
    } catch (err) {
      setError('Ein unerwarteter Fehler ist aufgetreten.')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(prev => ({ ...prev, [type]: true }))
      // Reset nach 2 Sekunden
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [type]: false }))
      }, 2000)
    } catch (error) {
      console.error('Kopieren fehlgeschlagen:', error)
    }
  }

  const handleDownloadPDF = async () => {
    if (!result?.data?.pdf_download_url) return

    setDownloading(true)
    try {
      const response = await fetch(result.data.pdf_download_url)
      
      if (!response.ok) {
        throw new Error('Download fehlgeschlagen')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // PDF-Dateiname basierend auf UID
      const filename = `vatly-${result.data.query}.pdf`
      link.download = filename
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download Error:', error)
      setError('PDF-Download fehlgeschlagen. Bitte versuchen Sie es erneut.')
    } finally {
      setDownloading(false)
    }
  }

  const getResultIcon = () => {
    if (!result) return null
    
    if (result.type === 'valid') {
      return <CheckCircle className="w-8 h-8 text-green-500" />
    } else if (result.type === 'invalid') {
      return <XCircle className="w-8 h-8 text-orange-500" />
    } else {
      return <XCircle className="w-8 h-8 text-red-500" />
    }
  }

  const getResultColor = () => {
    if (!result) return ''
    
    if (result.type === 'valid') {
      return 'border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800'
    } else if (result.type === 'invalid') {
      return 'border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800'
    } else {
      return 'border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800'
    }
  }

  const getResultTextColor = () => {
    if (!result) return ''
    
    if (result.type === 'valid') {
      return 'text-green-800 dark:text-green-200'
    } else if (result.type === 'invalid') {
      return 'text-orange-800 dark:text-orange-200'
    } else {
      return 'text-red-800 dark:text-red-200'
    }
  }

  const getResultSubTextColor = () => {
    if (!result) return ''
    
    if (result.type === 'valid') {
      return 'text-green-700 dark:text-green-300'
    } else if (result.type === 'invalid') {
      return 'text-orange-700 dark:text-orange-300'
    } else {
      return 'text-red-700 dark:text-red-300'
    }
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="card">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Vatly
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Überprüfen Sie Umsatzsteuernummern schnell und zuverlässig
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="uid" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Umsatzsteuer-ID Nummer
            </label>
            <div className="relative">
              <input
                id="uid"
                type="text"
                value={uid}
                onChange={(e) => setUid(e.target.value.toUpperCase())}
                placeholder="z.B. DE123456789"
                className="input-field pr-12"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-primary-600 hover:text-primary-700 disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
              </button>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !uid.trim()}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Überprüfe UID...
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                UID überprüfen
              </>
            )}
          </button>
        </form>

        {result && (
          <div className={`mt-6 p-6 rounded-lg border ${getResultColor()} animate-slide-up`}>
            <div className="flex items-start gap-4">
              {getResultIcon()}
              <div className="flex-1">
                <h3 className={`font-semibold text-lg ${getResultTextColor()}`}>
                  {result.type === 'valid' ? 'UID gefunden' : 
                   result.type === 'invalid' ? 'UID ungültig' : 
                   'Fehler'}
                </h3>
                <p className={`text-sm mt-1 ${getResultSubTextColor()}`}>
                  {result.message}
                </p>
                
                {result.data && (
                  <div className="mt-4 space-y-3">
                    {result.type === 'valid' && result.data.valid === true && (
                      <>
                        <div className="bg-white dark:bg-dark-700 rounded-lg p-4 border border-gray-200 dark:border-dark-600">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-3">Unternehmensdaten</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">UID:</span>
                              <span className="font-mono font-medium text-gray-900 dark:text-white">
                                {result.data.query}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Land:</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {result.data.country_code}
                              </span>
                            </div>
                            {result.data.company_name && (
                              <div className="border-t border-gray-200 dark:border-dark-600 pt-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-gray-600 dark:text-gray-400">Unternehmen:</span>
                                  <button
                                    onClick={() => handleCopyToClipboard(result.data.company_name, 'company')}
                                    className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                                    title="Unternehmensname kopieren"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </div>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {result.data.company_name}
                                </span>
                                {copied.company && (
                                  <span className="text-xs text-green-600 dark:text-green-400 ml-2">
                                    ✓ Kopiert!
                                  </span>
                                )}
                              </div>
                            )}
                            {result.data.company_address && (
                              <div className="border-t border-gray-200 dark:border-dark-600 pt-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-gray-600 dark:text-gray-400">Adresse:</span>
                                  <button
                                    onClick={() => handleCopyToClipboard(result.data.company_address, 'address')}
                                    className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                                    title="Adresse kopieren"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </div>
                                <span className="text-gray-900 dark:text-white whitespace-pre-line">
                                  {result.data.company_address}
                                </span>
                                {copied.address && (
                                  <span className="text-xs text-green-600 dark:text-green-400 ml-2">
                                    ✓ Kopiert!
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* PDF Download Button */}
                        {result.data.pdf_download_url && (
                          <div className="mt-4">
                            <button
                              onClick={handleDownloadPDF}
                              disabled={downloading}
                              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {downloading ? (
                                <>
                                  <Loader className="w-4 h-4 animate-spin" />
                                  PDF wird heruntergeladen...
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4" />
                                  UID-Bestätigung als PDF herunterladen
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                    
                    {result.type === 'invalid' && result.data.valid === false && (
                      <div className="bg-white dark:bg-dark-700 rounded-lg p-4 border border-gray-200 dark:border-dark-600">
                        <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2">Ungültige UID</h4>
                        <div className="text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Eingegebene UID:</span>
                            <span className="font-mono font-medium text-orange-700 dark:text-orange-300">
                              {result.data.query}
                            </span>
                          </div>
                          <p className="mt-2 text-orange-700 dark:text-orange-300">
                            Diese UID ist nicht gültig oder existiert nicht in der Datenbank.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default UIDChecker
